// src/hooks/useAuthFlow.js
import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { normalizeEmail, analyzeMethods, buildProviderAdvice, buildResetAdvice } from '../utils/authProviders';
import { mapFirebaseErrorToMessage } from '../utils/authErrors';
import { auth } from '../config/firebase';
import { passwordResetService } from '../services/passwordResetService';

export default function useAuthFlow() {
  const {
    login,
    register,
    loginWithGoogle,
    registerWithGoogle,
    getSignInMethodsForEmail,
    resetPassword,
  } = useAuth();

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [googleOnly, setGoogleOnly] = useState(false);

  const clearMessages = useCallback(() => {
    setErrorMsg('');
    setInfoMsg('');
    setGoogleOnly(false);
  }, []);

  const loginWithEmail = useCallback(async ({ email, password, onSuccess }) => {
    clearMessages();
    setLoading(true);
    try {
      const emailNorm = normalizeEmail(email);
      const methods = await getSignInMethodsForEmail(emailNorm);
      if (process.env.NODE_ENV !== 'production') {
        // Aide au debug local
        // eslint-disable-next-line no-console
        console.debug('[AuthFlow] login methods for', emailNorm, methods);
        // eslint-disable-next-line no-console
        console.debug('[AuthFlow] firebase project', auth?.app?.options?.projectId, 'authDomain', auth?.app?.options?.authDomain);
      }
      // Ne bloquer que pour les comptes Google-only; sinon tenter le login
      const analysis = analyzeMethods(methods);
      if (analysis.hasGoogle && !analysis.hasPassword) {
        const advice = buildProviderAdvice(analysis);
        if (advice) setErrorMsg(advice.message);
        setGoogleOnly(true);
        return false;
      }
      await login(emailNorm, password);
      onSuccess?.();
      return true;
    } catch (err) {
      try {
        // Fallback: si l'appel a échoué et que c'est possiblement un compte Google-only,
        // on re-vérifie les providers pour afficher un message pertinent.
        const emailNorm = normalizeEmail(email);
        const methods = await getSignInMethodsForEmail(emailNorm);
        const analysis = analyzeMethods(methods);
        if (analysis.hasGoogle && !analysis.hasPassword) {
          const advice = buildProviderAdvice(analysis);
          if (advice) setErrorMsg(advice.message);
          setGoogleOnly(true);
          return false;
        }
        if (analysis.isEmpty) {
          // Aucun provider connu → suggérer l'inscription explicitement
          const advice = buildProviderAdvice(analysis);
          if (advice) setErrorMsg(advice.message);
          return false;
        }
      } catch (_) {
        // ignore fallback errors
      }
      setErrorMsg(mapFirebaseErrorToMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, [clearMessages, getSignInMethodsForEmail, login]);

  const registerWithEmail = useCallback(async ({ email, password, profileData, onSuccess }) => {
    clearMessages();
    setLoading(true);
    try {
      const emailNorm = normalizeEmail(email);
      const methods = await getSignInMethodsForEmail(emailNorm);
      const { hasPassword, hasGoogle } = analyzeMethods(methods);
      if (hasPassword) {
        setErrorMsg('Cet email est déjà utilisé. Essayez de vous connecter.');
        return false;
      }
      if (hasGoogle && !hasPassword) {
        setErrorMsg("Un compte existe déjà avec cet email via Google. Utilisez 'Se connecter avec Google'.");
        return false;
      }
      await register({
        email: emailNorm,
        password,
        profileData,
      });
      onSuccess?.();
      return true;
    } catch (err) {
      setErrorMsg(mapFirebaseErrorToMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, [clearMessages, getSignInMethodsForEmail, register]);

  const resetPasswordFlow = useCallback(async ({ email }) => {
    clearMessages();
    setLoading(true);
    try {
      const emailNorm = normalizeEmail(email);
      const useServer = String(process.env.REACT_APP_USE_SERVER_RESET || '').toLowerCase() === 'true';

      if (useServer) {
        // Use backend endpoint; avoids email provider/domain pitfalls on client
        const redirectUrl = process.env.REACT_APP_FIREBASE_RESET_REDIRECT_URL || (typeof window !== 'undefined' ? window.location.origin : undefined);
        const res = await passwordResetService.requestReset(emailNorm, redirectUrl);
        if (res.sent) {
          setInfoMsg('Email de réinitialisation envoyé. Vérifiez votre boîte de réception.');
          return true;
        }
        if (res.reason === 'google_only') {
          setErrorMsg("Ce compte utilise Google. La réinitialisation du mot de passe n'est pas applicable.");
          setGoogleOnly(true);
          return false;
        }
        if (res.reason === 'no_account') {
          // Si le backend ne masque pas l'existence des comptes
          setErrorMsg("Aucun compte trouvé pour cet email.");
          return false;
        }
        if (res.reason === 'bad_redirect') {
          setErrorMsg('Le domaine de redirection n’est pas autorisé ou invalide dans Firebase.');
          return false;
        }
        setErrorMsg('Impossible de traiter la demande de réinitialisation.');
        return false;
      } else {
        // Client SDK path (previous behavior)
        const methods = await getSignInMethodsForEmail(emailNorm);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[AuthFlow] reset methods for', emailNorm, methods);
        }
        const analysis = analyzeMethods(methods);
        const advice = buildResetAdvice(analysis);
        if (advice) {
          if (advice.type === 'info' && analysis.hasPassword) {
            await resetPassword(emailNorm);
          }
          advice.type === 'error' ? setErrorMsg(advice.message) : setInfoMsg(advice.message);
          return advice.type === 'info';
        }
        setErrorMsg('Impossible de traiter la demande de réinitialisation.');
        return false;
      }
    } catch (err) {
      setErrorMsg(mapFirebaseErrorToMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, [clearMessages, getSignInMethodsForEmail, resetPassword]);

  const googleAuth = useCallback(async ({ isRegister, profileData, onSuccess }) => {
    clearMessages();
    setLoading(true);
    try {
      if (isRegister) {
        await registerWithGoogle(profileData);
      } else {
        await loginWithGoogle();
      }
      onSuccess?.();
      return true;
    } catch (err) {
      setErrorMsg(mapFirebaseErrorToMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, [clearMessages, loginWithGoogle, registerWithGoogle]);

  return {
    loading,
    errorMsg,
    infoMsg,
    googleOnly,
    setErrorMsg,
    setInfoMsg,
    clearMessages,
    loginWithEmail,
    registerWithEmail,
    resetPasswordFlow,
    googleAuth,
  };
}
